import { useState } from "react";
import { useWatch } from "react-hook-form";
import { useHistory } from "react-router-dom";
import { t } from "@/i18n-js/instance";
import { useCourseCustomLabels } from "@/react/components/Spaces/CourseSpace/Shared/useCourseCustomLabels";
import type { CoursesLesson } from "@/react/types/CoursesLesson";
import { LessonNavigation } from "@circle-react/components/Spaces/CourseSpace/Shared/LessonNavigation";
import { useCurrentSpaceContext } from "@circle-react/contexts";
import { coursePaths } from "@circle-react/helpers/urlHelpers";
import { useCourseSections } from "@circle-react/hooks/courses/useCoursesApi";
import { Typography } from "@circle-react-uikit/Typography";
import { LessonContent } from "./LessonContent";
import { LessonNameEditor } from "./LessonNameEditor";
import { QuizContent } from "./QuizContent";

interface ContentEditorProps {
  lesson: CoursesLesson;
}

export const ContentEditor = ({ lesson }: ContentEditorProps) => {
  const name = useWatch({ name: "name" });
  const history = useHistory();
  const [isNameEditMode, setIsNameEditMode] = useState(false);
  const { data: space } = useCurrentSpaceContext();
  const { data: sectionsData } = useCourseSections(space?.id);
  const { translatedLabels } = useCourseCustomLabels();
  const orderedLessons =
    sectionsData?.records?.flatMap((section: any) =>
      section.lessons.map((l: any) => ({ ...l, section_id: section.id })),
    ) || [];

  const currentLessonIndex = orderedLessons.findIndex(
    (l: any) => l.id === lesson.id,
  );

  const isQuiz = lesson.quiz;

  return (
    <div
      className="grid-in-main w-full overflow-auto px-4"
      data-testid="lesson-content"
      data-ai-scrollable
    >
      <div className="mx-auto max-w-[800px] flex-1 space-y-8 py-6 md:py-8">
        <div
          className="flex items-start justify-between"
          data-testid="lesson-content-header"
        >
          <div className="flex-1 space-y-1">
            <Typography.LabelSm>
              {t("courses.edit.lesson.position", {
                label: translatedLabels.lesson.singular,
                current: currentLessonIndex + 1,
                total: orderedLessons.length,
              })}
            </Typography.LabelSm>
            {isNameEditMode ? (
              <LessonNameEditor setNameEditMode={setIsNameEditMode} />
            ) : (
              <button
                type="button"
                className="w-full cursor-text text-left"
                onClick={() => setIsNameEditMode(true)}
              >
                <Typography.TitleMd as="span">{name}</Typography.TitleMd>
              </button>
            )}
          </div>
          <LessonNavigation
            orderedLessons={orderedLessons}
            currentLessonIndex={currentLessonIndex}
            isEditing
            goToLesson={({ lessonId, sectionId, spaceSlug }) => {
              history.push(
                coursePaths.editLesson({
                  spaceSlug,
                  sectionId,
                  lessonId,
                }),
              );
            }}
          />
        </div>
        {isQuiz ? <QuizContent /> : <LessonContent lesson={lesson} />}
      </div>
    </div>
  );
};
